import React, { useState, useRef, useEffect } from "react";
import { db } from "./firebase";
import { collection, addDoc } from "firebase/firestore";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { useSnackbar } from "./SnackbarHandler";
import { callSearchApi, callApi } from "./api";
import { trackLesson, escapeLatexInJson, sanitizeInput } from "./functions";
import TeacherQuizDisplay from "./TeacherQuizDisplay";
import LoadingDialog from "./LoadingDialog";
import { handlePrintQuiz, handlePrint1X2Quiz } from "./printFunctions";

import Logger from "./Logger";

const Nutidsfragor = ({ user, theme, styles, setMinimized }) => {
  const [topic, setTopic] = useState("");
  const [quiz, setQuiz] = useState([]);
  const [currentQuizId, setCurrentQuizId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingQuestionsInDialog, setIsGeneratingQuestionsInDialog] =
    useState(false);
  const [progress, setProgress] = useState(0);

  const { showSnackbar } = useSnackbar();
  const teacherQuizDisplayRef = useRef(null);

  // Default values for quiz settings
  const selectedLanguage = "svenska";
  const selectedModel = "Studera.AI";
  const numQuestions = 7;
  const questionType = "current-events";
  const category = "Välj kategori";
  const difficulty = "Medel";
  const aiInstructions = "";
  const isPublic = false;
  const includeTimer = false;
  const duration = 60;

  const simulateProgress = (startProgress, endProgress, duration) => {
    const stepSize = 0.5;
    const steps = (endProgress - startProgress) / stepSize;
    const stepDuration = duration / steps;
    let currentProgress = startProgress;

    const intervalId = setInterval(() => {
      currentProgress += stepSize;
      if (currentProgress >= endProgress) {
        clearInterval(intervalId);
      } else {
        setProgress(currentProgress);
      }
    }, stepDuration);

    return () => clearInterval(intervalId);
  };

  useEffect(() => {
    setMinimized(true);
  }, []);

  // Replace the existing generateQuizWithCurrentInfo function with this improved version:

  // Ersätt din generateQuizWithCurrentInfo-funktion i Nutidsfragor.js med denna:

  const generateQuizWithCurrentInfo = async (
    topic,
    numQuestions = 7,
    selectedLanguage = "svenska",
    userId = null,
    includeHints = false,
  ) => {
    try {
      setProgress(0);
      setIsGenerating(true);
      setCurrentQuizId(null);

      // STEG 1: Samla information med search API
      const searchMessages = [
        {
          role: "system",
          content: `Du är en expert på att söka efter aktuell information. Samla den senaste informationen om ämnet (minst 20 händelser) och presentera den som faktapunkter. Ingen framtida information, endast händelser som skett.`,
        },
        {
          role: "user",
          content: `Sök efter följande information: ${topic}. Fokusera på de senaste händelserna, nyheterna och utvecklingen. Presentera informationen som tydliga faktapunkter med datum och detaljer. Ingen framtida information, endast händelser som skett.`,
        },
      ];

      let stopProgressSimulation = simulateProgress(0, 45, 8000);

      const searchResponse = await callSearchApi(
        searchMessages,
        8000,
        true,
        userId,
        0.7,
        null,
        "medium",
      );

      if (stopProgressSimulation) {
        stopProgressSimulation();
      }
      setProgress(50);

      if (!searchResponse) {
        throw new Error("No response from search API");
      }

      const searchContent =
        searchResponse.data.choices[0].message.content.trim();

      Logger.log("Search response:", searchContent);

      const quizMessages = [
        {
          role: "system",
          content: `Du är en expert på att skapa engagerande och varierande nutidsfrågor. Du ska skapa EXAKT ${numQuestions} unika quiz-frågor baserat på den givna informationen. 
          Varje fråga ska vara 4 meningar som bygger upp kontexten innan själva frågan ställs. Detta är klassiskt nutidsfrågformat. 
          Formulera frågorna så att svaret INTE framgår av frågetexten.
      `,
        },
        {
          role: "user",
          content: `
Generate a quiz with ${numQuestions} questions based on this information: \n\n${searchContent}\n\n. Do not use  Each question should be EXACTLY 4 sentences long and worded in a engaging way. Formulera frågorna så att svaret INTE framgår av frågetexten - gör dem utmanande. Se till att svarsalternativen är balanserade och realistiska för att göra frågan mer utmanande. The quiz should be in ${selectedLanguage}. Each question should be a JSON object with:
- A "question" field 
- An "options" field containing exactly 3 options, each consisting of EXACTLY 1 OR MAXIMUM 2 words
- A "correct" field with the right answer

Ensure all questions are returned in a single JSON array with the following format:


[
  {
    "question": "Who won the FIFA World Cup in 2018?",
    "options": ["Brazil", "France", "Germany"],
    "correct": "France"
  },
  {
    "question": "Which country hosted the 2022 FIFA World Cup?",
    "options": ["Qatar", "Russia", "Brazil"],
    "correct": "Qatar"
  }
  // Etc...
]`,
        },
      ];

      stopProgressSimulation = simulateProgress(50, 90, 7000);

      // Använd vanlig API för quiz-generering
      const quizResponse = await callApi(quizMessages, 3000, true, userId);

      if (stopProgressSimulation) {
        stopProgressSimulation();
      }
      setProgress(100);

      Logger.log("Quiz response:", quizResponse);

      if (!quizResponse) {
        throw new Error("No response from quiz API");
      }

      // Extract the response content
      const messageContent =
        quizResponse.data.choices[0].message.content.trim();

      let jsonContent = messageContent;
      if (jsonContent.startsWith("```json")) {
        jsonContent = jsonContent.slice(7, -3).trim();
      } else if (jsonContent.startsWith("```")) {
        jsonContent = jsonContent.slice(3, -3).trim();
      }
      jsonContent = escapeLatexInJson(jsonContent);

      Logger.log("Parsed JSON content:", jsonContent);

      let parsedQuiz = JSON.parse(jsonContent);

      const finalQuiz = parsedQuiz;
      Logger.log("Final quiz:", finalQuiz);

      // Save to database
      const sanitizedQuizName = sanitizeInput(`Nutidsfrågor: ${topic}`);

      const docRef = await addDoc(collection(db, "quiz"), {
        userId: user.uid,
        results: finalQuiz,
        quizName: sanitizedQuizName,
        createdAt: Date.now(),
        byAdmin: "No",
        isPublic: isPublic,
        questionType: questionType,
        examResults: null,
        category,
        difficulty,
        aiInstructions,
        lastTrainingSession: Date.now(),
        language: selectedLanguage,
        aiModel: selectedModel,
        upvotes: 0,
        downvotes: 0,
        userVotes: {},
        includeTimer: includeTimer,
        duration: duration,
      });

      setQuiz(finalQuiz);
      setCurrentQuizId(docRef.id);
      setIsGeneratingQuestionsInDialog(true);
      showSnackbar("Nutidsfrågor genererade!", "success");

      await trackLesson(user.uid, "Nutidsfragor.js", null, {
        lessonTheme: "current-events",
      });
    } catch (error) {
      Logger.log("Error generating quiz:", error);
      showSnackbar("Knakar i fogarna: Försök generera quizzet igen.", "info");
    } finally {
      setIsGenerating(false);
      setProgress(0);
    }
  };

  const handleQuizUpdate = (updatedQuiz) => {
    setQuiz(updatedQuiz);
  };

  const reset = () => {
    setTopic("");
    setQuiz([]);
    setCurrentQuizId(null);
    setIsGeneratingQuestionsInDialog(false);
  };

  const handleSave = async () => {
    try {
      const documentData = {
        results: quiz,
        userId: user.uid,
        quizName: `Nutidsfrågor: ${topic}`,
        createdAt: Date.now(),
        byAdmin: "No",
        isPublic: isPublic,
        questionType: questionType,
        category: category,
        difficulty: difficulty,
        aiInstructions: aiInstructions,
        lastTrainingSession: null,
        language: selectedLanguage,
        examResults: null,
        aiModel: selectedModel,
        upvotes: 0,
        downvotes: 0,
        userVotes: {},
        includeTimer,
        duration,
      };

      await addDoc(collection(db, "quiz"), documentData);
      Logger.log("Sparar quiz:", JSON.stringify(documentData, null, 2));
      showSnackbar(
        "Du har nu sparat quizzet och kan hitta det i 'Mina quiz'.",
        "success",
      );
    } catch (error) {
      Logger.error("Error saving quiz:", error);
      showSnackbar("Ett fel uppstod när quizzet skulle sparas.", "error");
    }
  };

  return (
    <Box sx={{ ...styles.container, textAlign: "left" }}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "60%" },
          margin: "0 auto",
        }}
      >
        <Typography variant="h1" gutterBottom sx={{ ...styles.newH1 }}>
          Nutidsfrågor
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, textAlign: "center" }}>
          Generera nutidsfrågor med aktuell information baserat på sökning på
          internet.
        </Typography>

        <Box sx={{ ...styles.freshBox, padding: "30px" }}>
          <Typography
            variant="formLabelSinUnderline"
            sx={{ fontWeight: "500", mb: 2 }}
          >
            Ämne att söka efter
          </Typography>

          <TextField
            placeholder="t.ex. 'Trumps senaste uttalanden', 'Ukraina kriget idag', 'AI utveckling 2025'"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ mb: 3 }}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !isGenerating) {
                generateQuizWithCurrentInfo(
                  topic,
                  numQuestions,
                  selectedLanguage,
                  user.uid,
                  false,
                );
              }
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              generateQuizWithCurrentInfo(
                topic,
                numQuestions,
                selectedLanguage,
                user.uid,
                false,
              )
            }
            disabled={isGenerating || !topic.trim()}
            startIcon={
              isGenerating ? (
                <CircularProgress sx={{ color: "white" }} size={18} />
              ) : (
                <AutoFixHighIcon />
              )
            }
            fullWidth
            sx={{ py: 2 }}
          >
            {isGenerating
              ? "Genererar nutidsfrågor..."
              : "Generera nutidsfrågor"}
          </Button>

          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            Skapar 7 frågor med aktuell information från internet. Tar cirka
            30-60 sekunder.
          </Typography>
        </Box>

        {isGeneratingQuestionsInDialog && quiz.length > 0 && !isGenerating && (
          <div ref={teacherQuizDisplayRef}>
            <TeacherQuizDisplay
              includeTimer={includeTimer}
              duration={duration}
              user={user}
              quiz={quiz}
              quizId={currentQuizId}
              onQuizUpdate={handleQuizUpdate}
              selectedQuiz={{
                userId: user.uid,
                questionType: questionType,
                category: category,
                difficulty: difficulty,
              }}
              quizName={`Nutidsfrågor: ${topic}`}
              questionType={questionType}
              styles={styles}
              onClose={() => reset()}
              category={category}
              difficulty={difficulty}
              aiInstructions={aiInstructions}
              selectedLanguage={selectedLanguage}
              selectedModel={selectedModel}
              editMode={true}
              onSave={handleSave}
              onPrint={() =>
                handlePrint1X2Quiz(
                  quiz,
                  `Nutidsfrågor: ${topic}`,
                  questionType,
                  theme.palette.primary.main,
                )
              }
            />
          </div>
        )}
      </Box>

      <LoadingDialog
        open={isGenerating}
        onClose={() => {}}
        progress={progress}
        progressLabel={"Genererar nutidsfrågor — tar 30-60 sekunder..."}
        title="Genererar nutidsfrågor. Snart klart..."
        snackbarMessage="Genereringen är snart klar. Vänligen vänta."
        snackbarSeverity="info"
        progressBarHeight="15px"
        progressBarFontSize="0.9rem"
        coffee={false}
      />
    </Box>
  );
};

export default Nutidsfragor;
